import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import { useDynamicEngine, getLabelRegistry } from "@/lib/dynamicEngine";
import NexWordmark from "@/components/NexWordmark";
import { ArrowLeft, Search, Type, RotateCcw, Save, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";

/**
 * Editable UI Labels — Phase 3 of Dynamic Engine.
 *
 * The client-side registry (populated by useLabel(...) calls throughout the app)
 * is the source of truth for what labels exist. Whatever the user has visited
 * during this session becomes editable here. To seed the registry, the admin
 * dashboard imports the LABEL_KEYS module below at boot so every managed label
 * is registered even if the operator hasn't navigated to the page it lives on.
 */

const LabelsEditorPage = () => {
  const { labels, reloadLabels } = useDynamicEngine();
  const [drafts, setDrafts] = useState({});      // key → currentDraft text
  const [dirty, setDirty]   = useState({});      // key → true
  const [busyKey, setBusyKey] = useState(null);
  const [search, setSearch] = useState("");

  // Poll the registry periodically so any newly-rendered page that called
  // useLabel() shows up here without a hard refresh. Cheap — the Map is
  // synchronous and typically has <50 entries.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1500);
    return () => clearInterval(iv);
  }, []);
  const registry = useMemo(() => getLabelRegistry(), [labels, tick]);

  useEffect(() => {
    // Seed drafts from overrides (or default) so inputs are controlled.
    const initial = {};
    registry.forEach(({ key, default: def }) => {
      initial[key] = labels[key] !== undefined ? labels[key] : def;
    });
    setDrafts(initial);
    setDirty({});
  }, [registry, labels]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return registry;
    return registry.filter(({ key, default: def }) =>
      key.toLowerCase().includes(q) || (def || "").toLowerCase().includes(q)
    );
  }, [registry, search]);

  const onChange = (key, val) => {
    setDrafts((prev) => ({ ...prev, [key]: val }));
    setDirty((prev) => ({ ...prev, [key]: true }));
  };

  const saveOne = async (key) => {
    setBusyKey(key);
    try {
      await apiClient.put(`/admin/labels`, { key, value: drafts[key] });
      await reloadLabels();
      setDirty((prev) => ({ ...prev, [key]: false }));
      toast.success("Label saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyKey(null); }
  };

  const resetOne = async (key, def) => {
    setBusyKey(key);
    try {
      await apiClient.delete(`/admin/labels/${encodeURIComponent(key)}`);
      await reloadLabels();
      setDrafts((prev) => ({ ...prev, [key]: def }));
      setDirty((prev) => ({ ...prev, [key]: false }));
      toast.success("Reset to default");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyKey(null); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
            <ArrowLeft size={14} /> <span className="mono-body text-[11px] uppercase tracking-[0.2em]">Admin</span>
          </Link>
          <span className="text-nex-muted">·</span>
          <NexWordmark size="text-lg" />
          <div className="ml-auto flex items-center gap-2 text-nex-chrome text-xs">
            <Type size={14} className="text-nex-gold" />
            <span className="mono-body uppercase tracking-[0.2em] text-[11px]">Editable Labels</span>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8" data-testid="labels-editor">
        <div className="card-industrial corner-tick p-6 mb-6">
          <div className="mono-label mb-2">// UI COPY</div>
          <h1 className="serif-head text-3xl mb-2">Editable labels</h1>
          <p className="text-nex-chrome text-sm mb-4 max-w-2xl">
            Every registered heading, button, and section label in the admin
            console can be renamed here. Blank a value out and it falls back
            to the code default. Changes go live for both partners immediately.
          </p>
          <div className="flex items-center gap-2">
            <Search size={14} className="text-nex-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by key or default text…"
              className="input-industrial flex-1 max-w-md"
              data-testid="labels-search"
            />
            <span className="mono-body text-[10px] text-nex-muted ml-auto">
              {filtered.length} / {registry.length} labels
            </span>
          </div>
        </div>

        {registry.length === 0 ? (
          <div className="card-industrial corner-tick p-10 text-center">
            <Tag className="mx-auto text-nex-muted mb-3" />
            <div className="text-nex-chrome text-sm">
              No labels registered yet. Navigate to any admin page — labels register
              themselves as they render. Then come back here to customize them.
            </div>
          </div>
        ) : (
          <div className="card-industrial corner-tick p-6" data-testid="labels-list">
            <div className="space-y-3">
              {filtered.map(({ key, default: def }) => {
                const overridden = labels[key] !== undefined && labels[key] !== "" && labels[key] !== def;
                const isDirty = !!dirty[key];
                return (
                  <div key={key} className="border border-nex-border p-3" data-testid={`label-row-${key}`}>
                    <div className="flex items-center justify-between mb-2 gap-3">
                      <div className="mono-body text-[11px] text-nex-gold uppercase tracking-[0.15em] truncate">{key}</div>
                      {overridden && (
                        <span className="mono-body text-[9px] uppercase text-emerald-300 border border-emerald-300/30 px-1.5 py-0.5">
                          Custom
                        </span>
                      )}
                    </div>
                    <div className="mono-body text-[10px] text-nex-muted mb-2">Default: {def}</div>
                    <div className="flex items-center gap-2">
                      <input
                        value={drafts[key] ?? ""}
                        onChange={(e) => onChange(key, e.target.value)}
                        placeholder={def}
                        className="input-industrial flex-1"
                        data-testid={`label-input-${key}`}
                      />
                      <button
                        onClick={() => saveOne(key)}
                        disabled={!isDirty || busyKey === key}
                        className="tick-btn-primary text-xs !py-2 !px-3"
                        data-testid={`label-save-${key}`}
                      >
                        {busyKey === key ? <Loader2 size={12} className="animate-spin" /> : <><Save size={12} /> Save</>}
                      </button>
                      <button
                        onClick={() => resetOne(key, def)}
                        disabled={!overridden || busyKey === key}
                        className="tick-btn-secondary text-xs !py-2 !px-3 disabled:opacity-30"
                        title="Reset to default"
                        data-testid={`label-reset-${key}`}
                      >
                        <RotateCcw size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LabelsEditorPage;
